/**
 * WordImportFlow — /question-banks/import
 *
 * Two-step, full-page Word (.docx) import:
 *   1. Upload   — drag & drop or pick a .docx, send it to ?preview=true
 *   2. Preview  — edit the parsed questions inline, name the bank, save it
 *
 * Saving creates a brand-new QuestionBank, so the flow never touches an
 * existing bank id. On success it lands on the new bank's question list.
 */
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { previewWordImport, saveWordImport } from '@/api/resources'
import { Button, Badge, FIELD_CLS } from '@/components/ui/primitives'
import { ArrowLeft, FileText, Trash2, Upload, X } from 'lucide-react'

const MAX_BYTES = 10 * 1024 * 1024

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** Live status of a question — recomputed on every edit, not taken from the server. */
function questionStatus(q) {
  const correct = (q.options || []).filter(o => o.is_correct).length
  if (correct === 0) return 'warning'
  if (correct > 1) return 'error'
  return 'ok'
}

// ─────────────────────────────────────────────────────────────────────────────
//  Step 1 — Upload
// ─────────────────────────────────────────────────────────────────────────────
function UploadStep({ file, setFile, onPreview, loading, error, setError }) {
  const { t } = useTranslation()
  const inputRef = useRef(null)
  const [dragging, setDragging] = useState(false)

  function accept(candidate) {
    if (!candidate) return
    if (!candidate.name.toLowerCase().endsWith('.docx')) {
      setError(t('wordImport.errorExtension', 'Faqat .docx fayllar qabul qilinadi.'))
      return
    }
    if (candidate.size > MAX_BYTES) {
      setError(t('wordImport.errorSize', 'Fayl juda katta. Maksimal hajm — 10 MB.'))
      return
    }
    setError(null)
    setFile(candidate)
  }

  return (
    <div className="space-y-4">
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); accept(e.dataTransfer.files?.[0]) }}
        onClick={() => inputRef.current?.click()}
        className="rounded-2xl border-2 border-dashed px-6 py-14 text-center cursor-pointer transition-colors"
        style={{
          borderColor: dragging ? 'var(--coral)' : 'var(--border)',
          background: dragging ? 'var(--bg-soft)' : 'transparent',
        }}
      >
        <div className="flex flex-col items-center gap-3">
          <Upload size={38} style={{ color: 'var(--ink-3)' }} />
          <p className="font-semibold" style={{ color: 'var(--ink)' }}>
            {t('wordImport.dropzone', 'Faylni bu yerga tashlang yoki tanlash uchun bosing')}
          </p>
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
            {t('wordImport.dropzoneHint', 'Faqat .docx, maksimal 10 MB')}
          </p>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          className="hidden"
          onChange={e => { accept(e.target.files?.[0]); e.target.value = '' }}
        />
      </div>

      {file && (
        <div className="flex items-center gap-3 rounded-xl border px-4 py-3" style={{ borderColor: 'var(--border)' }}>
          <FileText size={18} style={{ color: 'var(--ink-3)' }} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate" style={{ color: 'var(--ink)' }}>{file.name}</p>
            <p className="text-xs" style={{ color: 'var(--ink-3)' }}>{formatSize(file.size)}</p>
          </div>
          <button
            type="button"
            onClick={() => setFile(null)}
            className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors"
            style={{ color: 'var(--ink-3)' }}
            title={t('common.delete', "O'chirish")}
          ><X size={16} /></button>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>
      )}

      <div className="flex justify-end">
        <Button onClick={onPreview} disabled={!file || loading} loading={loading}>
          {loading ? t('wordImport.parsing', 'Tahlil qilinmoqda...') : t('common.preview', "Ko'rish")}
        </Button>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
//  Step 2 — Preview & edit
// ─────────────────────────────────────────────────────────────────────────────
function QuestionCard({ question, position, onChange, onRemove }) {
  const { t } = useTranslation()
  const status = questionStatus(question)

  function setOption(idx, patch) {
    onChange({ ...question, options: question.options.map((o, i) => i === idx ? { ...o, ...patch } : o) })
  }

  function setCorrect(idx) {
    onChange({ ...question, options: question.options.map((o, i) => ({ ...o, is_correct: i === idx })) })
  }

  return (
    <div className="rounded-2xl border p-4 space-y-3" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
      <div className="flex items-start gap-3">
        <span className="mt-2 text-sm font-bold flex-shrink-0" style={{ color: 'var(--ink-3)' }}>{position}.</span>
        <textarea
          value={question.text}
          onChange={e => onChange({ ...question, text: e.target.value })}
          rows={2}
          className={`${FIELD_CLS} flex-1 min-w-0`}
          placeholder={t('wordImport.questionText', 'Savol matni')}
        />
        <button
          type="button"
          onClick={onRemove}
          className="tbl-btn tbl-danger flex-shrink-0 mt-1"
          title={t('wordImport.removeQuestion', "Savolni o'chirish")}
        ><Trash2 size={15} /></button>
      </div>

      <div className="space-y-2 pl-6">
        {question.options.map((opt, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <input
              type="radio"
              name={`correct-${question._id}`}
              checked={!!opt.is_correct}
              onChange={() => setCorrect(idx)}
              className="w-4 h-4 flex-shrink-0 accent-[var(--coral)]"
              title={t('wordImport.markCorrect', "To'g'ri javob sifatida belgilash")}
            />
            <Badge variant={opt.is_correct ? 'green' : 'gray'}>{opt.letter}</Badge>
            <input
              value={opt.text}
              onChange={e => setOption(idx, { text: e.target.value })}
              className={`${FIELD_CLS} flex-1 min-w-0`}
              placeholder={t('wordImport.optionText', 'Javob varianti')}
            />
          </div>
        ))}
      </div>

      {status === 'warning' && (
        <Badge variant="amber">{t('wordImport.noCorrect', "To'g'ri javob belgilanmagan")}</Badge>
      )}
      {status === 'error' && (
        <Badge variant="red">{t('wordImport.multipleCorrect', "Bir nechta to'g'ri javob")}</Badge>
      )}
    </div>
  )
}

function PreviewStep({ questions, setQuestions, name, setName, onSave, onCancel, saving, error }) {
  const { t } = useTranslation()

  const warnings = questions.filter(q => questionStatus(q) === 'warning').length
  const errors   = questions.filter(q => questionStatus(q) === 'error').length
  const canSave  = !!name.trim() && questions.length > 0 && errors === 0

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border p-4 space-y-3" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--ink-2)' }}>
            {t('wordImport.bankName', 'Savollar bazasi nomi')} *
          </label>
          <input value={name} onChange={e => setName(e.target.value)} className={FIELD_CLS} />
        </div>

        <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
          {t('wordImport.summary', '{{n}} ta savol', { n: questions.length })}
          {' · '}
          {t('wordImport.summaryWarnings', '{{n}} ta ogohlantirish', { n: warnings })}
          {' · '}
          {t('wordImport.summaryErrors', '{{n}} ta xatolik', { n: errors })}
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>
        )}

        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={onCancel}>{t('common.cancel', 'Bekor qilish')}</Button>
          <Button onClick={onSave} disabled={!canSave || saving} loading={saving}>
            {saving ? t('common.saving', 'Saqlanmoqda...') : t('common.save', 'Saqlash')}
          </Button>
        </div>
      </div>

      {questions.map((q, idx) => (
        <QuestionCard
          key={q._id}
          question={q}
          position={idx + 1}
          onChange={next => setQuestions(prev => prev.map(p => p._id === q._id ? next : p))}
          onRemove={() => setQuestions(prev => prev.filter(p => p._id !== q._id))}
        />
      ))}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
//  Flow
// ─────────────────────────────────────────────────────────────────────────────
export default function WordImportFlow() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [step,      setStep]      = useState('upload')
  const [file,      setFile]      = useState(null)
  const [questions, setQuestions] = useState([])
  const [name,      setName]      = useState('')
  const [loading,   setLoading]   = useState(false)
  const [saving,    setSaving]    = useState(false)
  const [error,     setError]     = useState(null)
  const [toast,     setToast]     = useState(null)

  function apiError(e, fallback) {
    return e.response?.data?.detail || e.message || fallback
  }

  async function handlePreview() {
    setLoading(true); setError(null)
    try {
      const form = new FormData()
      form.append('file', file)
      const { data } = await previewWordImport(form)
      const parsed = data.questions || []
      if (parsed.length === 0) {
        setError(t('wordImport.noQuestions', 'Faylda savollar topilmadi. Fayl formatini tekshiring.'))
        return
      }
      setQuestions(parsed.map((q, idx) => ({ ...q, _id: `${idx}-${q.index}` })))
      setName(prev => prev || file.name.replace(/\.docx$/i, ''))
      setStep('preview')
    } catch (e) {
      setError(apiError(e, t('common.error', 'Xatolik yuz berdi')))
    } finally { setLoading(false) }
  }

  async function handleSave() {
    if (!name.trim()) {
      setError(t('wordImport.nameRequired', 'Baza nomini kiriting.'))
      return
    }
    if (questions.some(q => questionStatus(q) === 'error')) {
      setError(t('wordImport.fixErrors', 'Saqlashdan oldin xatoliklarni tuzating.'))
      return
    }
    setSaving(true); setError(null)
    try {
      const payload = {
        name: name.trim(),
        questions: questions.map(({ _id, ...q }) => q),
      }
      const { data } = await saveWordImport(payload)
      setToast(t('wordImport.saved', '{{n}} ta savol muvaffaqiyatli saqlandi', { n: data.created }))
      setTimeout(() => navigate(`/questions?bank=${data.bank_id}`), 1200)
    } catch (e) {
      setError(apiError(e, t('common.error', 'Xatolik yuz berdi')))
      setSaving(false)
    }
  }

  function handleCancel() {
    setStep('upload')
    setQuestions([])
    setError(null)
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/question-banks')}
          className="p-2 rounded-xl transition-colors"
          style={{ color: 'var(--ink-3)' }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-soft)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--ink)' }}>
            {t('wordImport.title', 'Word fayldan import')}
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--ink-3)' }}>
            {step === 'upload'
              ? t('wordImport.stepUpload', 'Faylni yuklang')
              : t('wordImport.stepPreview', "Savollarni tekshiring va saqlang")}
          </p>
        </div>
      </div>

      {step === 'upload' ? (
        <div className="rounded-2xl border p-6" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <UploadStep
            file={file}
            setFile={setFile}
            onPreview={handlePreview}
            loading={loading}
            error={error}
            setError={setError}
          />
        </div>
      ) : (
        <PreviewStep
          questions={questions}
          setQuestions={setQuestions}
          name={name}
          setName={setName}
          onSave={handleSave}
          onCancel={handleCancel}
          saving={saving}
          error={error}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 rounded-xl px-4 py-3 text-sm font-medium text-white shadow-lg"
          style={{ background: 'var(--mint, #10b981)' }}>
          {toast}
        </div>
      )}
    </div>
  )
}
